/**
 * Sea route interpretation, shared by every surface that draws a shipment.
 *
 * Extracted verbatim from ConsignmentDetailPage so the operator view, the
 * customer-facing tracking link and the server-side public projection all read
 * a route the same way. A second copy of this logic is how the customer's map
 * and the internal one end up disagreeing about where the cargo is.
 *
 * Everything here is pure: no React, no DOM, no network. That is what lets it
 * run on the server as well as in the browser.
 */

import type {
  SeaTrackingContainer,
  SeaTrackingShipment,
  SeaVesselCurrentPosition,
} from '../../lib/api/types'
import { knownRouteCoordinates } from './known-ports'

export function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function readString(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'string' && entry.trim() ? entry.trim() : null
}

export function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function readRecordValue(value: Record<string, unknown> | null | undefined, key: string) {
  return readRecord(value?.[key])
}

export function readRecordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.map(readRecord).filter((entry): entry is Record<string, unknown> => Boolean(entry))
    : []
}

export function readNumber(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'number' && Number.isFinite(entry) ? entry : null
}

export function readFirstString(value: Record<string, unknown> | null | undefined, keys: string[]) {
  for (const key of keys) {
    const entry = readString(value, key)
    if (entry) return entry
  }
  return null
}

export function getLocationRecord(value: Record<string, unknown> | null | undefined) {
  return (
    readRecord(value?.location) ??
    readRecord(value?.port) ??
    readRecord(value?.terminal) ??
    readRecord(value?.facility)
  )
}

export function formatLocationName(value: Record<string, unknown> | null | undefined) {
  const record = readRecord(value)
  if (!record) return null
  const nestedLocation = getLocationRecord(record)
  return (
    readFirstString(record, [
      'location',
      'location_name',
      'port_name',
      'terminal_name',
      'facility_name',
      'name',
      'unlocode',
    ]) ||
    readFirstString(nestedLocation, [
      'name',
      'location_name',
      'port_name',
      'terminal_name',
      'facility_name',
      'city',
      'unlocode',
    ])
  )
}

export function formatLocationMeta(value: Record<string, unknown> | null | undefined) {
  const record = readRecord(value)
  if (!record) return null
  const nestedLocation = getLocationRecord(record)
  const country =
    readFirstString(record, ['country_name', 'country_code']) ||
    readFirstString(nestedLocation, ['country_name', 'country_code'])
  const unlocode = readString(record, 'unlocode') || readString(nestedLocation, 'unlocode')
  return [country, unlocode].filter(Boolean).join(' · ') || null
}

export function readFirstDate(value: Record<string, unknown> | null | undefined, keys: string[]) {
  return readFirstString(value, keys)
}

export function readNumberLike(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const parsed = Number(value.trim())
  return Number.isFinite(parsed) ? parsed : null
}

export function readFirstNumber(value: Record<string, unknown> | null | undefined, keys: string[]) {
  for (const key of keys) {
    const entry = readNumberLike(value?.[key])
    if (entry !== null) return entry
  }
  return null
}

export function readRouteCoordinates(value: Record<string, unknown> | null | undefined) {
  const record = readRecord(value)
  if (!record) return null
  const nestedLocation = getLocationRecord(record)
  const coordinates =
    readRecord(record.coordinates) ??
    readRecord(record.coordinate) ??
    readRecord(record.geo) ??
    readRecord(nestedLocation?.coordinates) ??
    readRecord(nestedLocation?.coordinate) ??
    readRecord(nestedLocation?.geo)
  const latitude =
    readFirstNumber(record, ['latitude', 'lat']) ??
    readFirstNumber(nestedLocation, ['latitude', 'lat']) ??
    readFirstNumber(coordinates, ['latitude', 'lat'])
  const longitude =
    readFirstNumber(record, ['longitude', 'lng', 'lon', 'long']) ??
    readFirstNumber(nestedLocation, ['longitude', 'lng', 'lon', 'long']) ??
    readFirstNumber(coordinates, ['longitude', 'lng', 'lon', 'long'])
  if (latitude === null || longitude === null) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { latitude, longitude }
}

export function describeContainerMovement(container: SeaTrackingContainer | undefined) {
  if (!container) return 'No container movement has been returned yet.'
  const movement = container.last_movement
  const event =
    readString(movement, 'event') ||
    readString(movement, 'status') ||
    readString(movement, 'description')
  const location =
    formatLocationName(movement) || container.pod_terminal_name || formatLocationName(container.pod)
  const vessel = [container.arriving_vessel_name, container.arriving_vessel_voyage_no]
    .filter(Boolean)
    .join(' ')

  return (
    [
      event ? formatLabel(event) : null,
      location ? `at ${location}` : null,
      vessel ? `via ${vessel}` : null,
    ]
      .filter(Boolean)
      .join(' ') || 'Container movement is available from the sea tracking feed.'
  )
}

export function getShipmentContainerCount(
  shipment: SeaTrackingShipment | null | undefined,
  containers: SeaTrackingContainer[],
) {
  return readNumber(shipment?.tracking || null, 'total_containers_count') ?? containers.length
}

export type SeaRouteStep = {
  label: string
  name: string
  meta: string | null
  date: string | null
  latitude: number | null
  longitude: number | null
}

export type SeaCurrentPosition = {
  name: string
  meta: string | null
  date: string | null
  event: string | null
  vessel: string | null
  voyage: string | null
  latitude: number | null
  longitude: number | null
  precision: 'LIVE' | 'RECENT' | 'STALE' | 'PROVIDER_REPORTED' | 'LAST_KNOWN'
  source: 'AISSTREAM' | 'DATA_DOCKED' | 'VISIWISE'
  speedOverGroundKnots: number | null
  courseOverGroundDegrees: number | null
}

export type RouteProgressStep = SeaRouteStep & {
  current: boolean
  completed: boolean
}

export type RouteMapPoint = {
  labels: string[]
  mapLabel: string
  name: string
  meta: string | null
  date: string | null
  latitude: number
  longitude: number
  current: boolean
  completed: boolean
}

export type RouteCoordinate = {
  latitude: number
  longitude: number
}

export type RoutePathSegment = {
  fromIndex: number
  toIndex: number
  coordinates: RouteCoordinate[]
}

export type SeaMovement = {
  key: string
  date: string | null
  location: string
  event: string
  mode: string
  vessel: string | null
}

export { knownRouteCoordinates } from './known-ports'

export const shapedRouteCorridors: Array<{
  from: string[]
  to: string[]
  waypoints: RouteCoordinate[]
}> = [
  {
    from: ['nanhai', 'nansha', 'cnnah'],
    to: ['mombasa', 'kemba'],
    waypoints: [
      { latitude: 20.7, longitude: 114.1 },
      { latitude: 12.0, longitude: 110.0 },
      { latitude: 3.0, longitude: 104.5 },
      { latitude: 1.2, longitude: 103.0 },
      { latitude: 5.6, longitude: 95.1 },
      { latitude: 3.8, longitude: 85.6 },
      { latitude: 1.0, longitude: 74.8 },
      { latitude: -1.7, longitude: 62.3 },
      { latitude: -3.8, longitude: 50.5 },
      { latitude: -4.2, longitude: 43.0 },
    ],
  },
  {
    from: ['shekou', 'shenzhen', 'cnshe', 'cnszx'],
    to: ['singapore', 'sgsin'],
    waypoints: [
      { latitude: 20.7, longitude: 114.1 },
      { latitude: 16.7, longitude: 112.6 },
      { latitude: 11.2, longitude: 110.1 },
      { latitude: 6.4, longitude: 106.6 },
      { latitude: 2.5, longitude: 104.1 },
    ],
  },
  {
    from: ['singapore', 'sgsin'],
    to: ['mombasa', 'kemba'],
    waypoints: [
      { latitude: 2.8, longitude: 101.2 },
      { latitude: 5.6, longitude: 95.1 },
      { latitude: 3.8, longitude: 85.6 },
      { latitude: 1.0, longitude: 74.8 },
      { latitude: -1.7, longitude: 62.3 },
      { latitude: -3.8, longitude: 50.5 },
      { latitude: -4.2, longitude: 43.0 },
    ],
  },
  {
    from: ['mombasa', 'kemba'],
    to: ['kampala', 'uganda', 'ugkla'],
    waypoints: [
      { latitude: -3.6, longitude: 38.8 },
      { latitude: -1.3, longitude: 36.8 },
      { latitude: -0.3, longitude: 34.7 },
    ],
  },
]

export function buildSeaRouteSteps(
  shipment: SeaTrackingShipment | null | undefined,
  containers: SeaTrackingContainer[],
): SeaRouteStep[] {
  const routeSummary = shipment?.route_summary ?? readRecord(shipment?.tracking)
  const firstContainer = containers[0]
  const transshipments = [
    ...readRecordArray(routeSummary?.transshipments),
    ...readRecordArray(firstContainer?.transshipments),
  ]

  return dedupeRouteSteps(
    [
      routeStep(
        'Place of Receipt',
        readRecordValue(routeSummary, 'pre') ?? firstContainer?.pre ?? null,
      ),
      routeStep('POL', readRecordValue(routeSummary, 'pol') ?? firstContainer?.pol ?? null),
      ...transshipments.map((step, index) => routeStep(`T/S ${index + 1}`, step)),
      routeStep('POD', readRecordValue(routeSummary, 'pod') ?? firstContainer?.pod ?? null),
      routeStep(
        'Place of Delivery',
        readRecordValue(routeSummary, 'pde') ?? firstContainer?.pde ?? null,
      ),
    ].filter((step): step is SeaRouteStep => Boolean(step)),
  )
}

export function routeStep(
  label: string,
  value: Record<string, unknown> | null | undefined,
): SeaRouteStep | null {
  const record = readRecord(value)
  if (!record) return null
  const name = formatLocationName(record)
  if (!name) return null
  const labelKey = label.toLowerCase()
  const voyageDateKeys =
    labelKey === 'pol'
      ? [
          'pol_departure_time',
          'departure_actual_time',
          'departure_time',
          'departure_estimated_time',
          'departed_at',
          'departure_at',
          'atd',
          'actual_at',
          'estimated_at',
        ]
      : [
          'pod_arrival_time',
          'pde_arrival_time',
          'arrival_actual_time',
          'arrival_actual_time_carrier',
          'arrival_time',
          'arrival_estimated_time',
          'arrival_estimated_time_carrier',
          'arrived_at',
          'arrival_at',
          'ata',
          'discharge_time',
          'gate_out',
          'actual_at',
          'estimated_at',
          'departure_actual_time',
          'departure_time',
          'departure_at',
          'atd',
          'pre_departure_time',
        ]
  const date = readFirstDate(record, voyageDateKeys)
  const coordinates = readRouteCoordinates(record)

  return {
    label,
    name,
    meta: formatLocationMeta(record),
    date,
    latitude: coordinates?.latitude ?? null,
    longitude: coordinates?.longitude ?? null,
  }
}

export function dedupeRouteSteps(steps: SeaRouteStep[]) {
  const seen = new Set<string>()
  return steps.filter((step) => {
    const key = `${step.label}:${step.name}:${step.meta ?? ''}`.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function buildRouteProgressSteps(
  routeSteps: SeaRouteStep[],
  containers: SeaTrackingContainer[],
): RouteProgressStep[] {
  const currentIndex = getCurrentRouteIndex(routeSteps, containers)
  return routeSteps.map((step, index) => {
    return {
      ...step,
      current: index === currentIndex,
      completed: index <= currentIndex,
    }
  })
}

export function buildRouteMapPoints(
  routeSteps: SeaRouteStep[],
  containers: SeaTrackingContainer[],
): RouteMapPoint[] {
  const progressSteps = buildRouteProgressSteps(routeSteps, containers)
  const points: RouteMapPoint[] = []

  for (const step of progressSteps) {
    const coordinates = getRouteStepCoordinates(step)
    if (!coordinates) continue
    const label = compactRouteLabel(step.label)
    const existing = points.find(
      (point) =>
        Math.abs(point.latitude - coordinates.latitude) < 0.005 &&
        Math.abs(point.longitude - coordinates.longitude) < 0.005,
    )

    if (existing) {
      if (!existing.labels.includes(label)) existing.labels.push(label)
      existing.mapLabel = combineRouteLabels(existing.labels)
      existing.current = existing.current || step.current
      existing.completed = existing.completed || step.completed
      if (step.current || (!existing.date && step.date)) {
        existing.name = step.name
        existing.meta = step.meta
        existing.date = step.date
      }
      continue
    }

    points.push({
      labels: [label],
      mapLabel: label,
      name: step.name,
      meta: step.meta,
      date: step.date,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      current: step.current,
      completed: step.completed,
    })
  }

  return points
}

export function getCurrentRouteIndex(
  routeSteps: SeaRouteStep[],
  containers: SeaTrackingContainer[],
) {
  const firstContainer = containers[0]
  const movementLocation =
    formatLocationName(firstContainer?.last_movement) || firstContainer?.pod_terminal_name || null
  if (movementLocation) {
    const normalizedLocation = movementLocation.toLowerCase()
    const matchingIndexes = routeSteps
      .map((step, index) => ({ step, index }))
      .filter(
        ({ step }) =>
          normalizedLocation.includes(step.name.toLowerCase()) ||
          step.name.toLowerCase().includes(normalizedLocation) ||
          Boolean(step.meta?.toLowerCase().includes(normalizedLocation)),
      )
      .map(({ index }) => index)
    // A place of receipt and port of loading can resolve to the same port. The
    // latest matching milestone is the useful operational position.
    if (matchingIndexes.length > 0) return matchingIndexes[matchingIndexes.length - 1]!
  }
  const datedIndexes = routeSteps
    .map((step, index) => ({ index, time: step.date ? new Date(step.date).getTime() : Number.NaN }))
    .filter((entry) => Number.isFinite(entry.time) && entry.time <= Date.now())
  if (datedIndexes.length > 0) return datedIndexes[datedIndexes.length - 1]!.index
  return routeSteps.length > 0 ? 0 : -1
}

export function getSeaCurrentPosition(
  containers: SeaTrackingContainer[],
): SeaCurrentPosition | null {
  let latest: SeaCurrentPosition | null = null

  for (const container of containers) {
    const raw = readRecord(container.raw)
    const candidates: Array<{
      record: Record<string, unknown> | null
      precision: SeaCurrentPosition['precision']
    }> = [
      { record: readRecord(raw?.current_position), precision: 'PROVIDER_REPORTED' },
      { record: readRecord(raw?.vessel_position), precision: 'PROVIDER_REPORTED' },
      { record: readRecord(raw?.ais_position), precision: 'PROVIDER_REPORTED' },
      { record: readRecord(raw?.current_location), precision: 'PROVIDER_REPORTED' },
      { record: readRecord(container.last_movement), precision: 'LAST_KNOWN' },
    ]

    for (const candidate of candidates) {
      if (!candidate.record) continue
      const coordinates = readRouteCoordinates(candidate.record)
      const name =
        formatLocationName(candidate.record) ||
        container.pod_terminal_name ||
        formatLocationName(container.pod)
      if (!name) continue
      const position: SeaCurrentPosition = {
        name,
        meta: formatLocationMeta(candidate.record),
        date: readFirstDate(candidate.record, [
          'position_time',
          'datetime',
          'timestamp',
          'event_at',
          'actual_at',
          'date',
          'updated_at',
        ]),
        event:
          readFirstString(candidate.record, ['event', 'status', 'description', 'movement']) || null,
        vessel:
          readFirstString(candidate.record, ['vessel_name', 'vessel']) ||
          container.arriving_vessel_name ||
          null,
        voyage:
          readFirstString(candidate.record, ['voyage_no', 'voyage_number', 'voyage']) ||
          container.arriving_vessel_voyage_no ||
          null,
        latitude: coordinates?.latitude ?? null,
        longitude: coordinates?.longitude ?? null,
        precision: candidate.precision,
        source: 'VISIWISE',
        speedOverGroundKnots: null,
        courseOverGroundDegrees: null,
      }
      const latestTime = latest?.date ? new Date(latest.date).getTime() : Number.NEGATIVE_INFINITY
      const positionTime = position.date
        ? new Date(position.date).getTime()
        : Number.NEGATIVE_INFINITY
      const improvesPrecision =
        latest?.precision === 'LAST_KNOWN' && position.precision === 'PROVIDER_REPORTED'
      const hasSamePrecisionAndIsNewer =
        latest?.precision === position.precision && positionTime > latestTime
      if (!latest || improvesPrecision || hasSamePrecisionAndIsNewer) {
        latest = position
      }
      if (position.precision === 'PROVIDER_REPORTED') break
    }
  }

  return latest
}

export function getVesselCurrentPosition(
  position: SeaVesselCurrentPosition | null | undefined,
  now: number,
): SeaCurrentPosition | null {
  if (
    position?.state !== 'AVAILABLE' ||
    typeof position.latitude !== 'number' ||
    typeof position.longitude !== 'number'
  ) {
    return null
  }

  const identity = [
    position.mmsi ? `MMSI ${position.mmsi}` : null,
    position.imo ? `IMO ${position.imo}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const positionTimestamp =
    validPositionTimestamp(position.observed_at) !== null
      ? (position.observed_at ?? null)
      : validPositionTimestamp(position.received_at) !== null
        ? (position.received_at ?? null)
        : position.observed_at || position.received_at || null

  return {
    name: position.vessel_name || identity || 'Tracked vessel',
    meta: identity || null,
    date: positionTimestamp,
    event:
      position.source === 'DATA_DOCKED'
        ? 'Data Docked vessel position report'
        : 'AIS position report',
    vessel: position.vessel_name,
    voyage: null,
    latitude: position.latitude,
    longitude: position.longitude,
    precision: classifyAisFreshness(positionTimestamp, now),
    source: position.source === 'DATA_DOCKED' ? 'DATA_DOCKED' : 'AISSTREAM',
    speedOverGroundKnots: position.speed_over_ground_knots ?? null,
    courseOverGroundDegrees: position.course_over_ground_degrees ?? null,
  }
}

export const AIS_LIVE_AGE_MS = 5 * 60 * 1_000

export const AIS_RECENT_AGE_MS = 30 * 60 * 1_000

export function validPositionTimestamp(value: string | null | undefined): number | null {
  if (!value) return null
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : null
}

export function classifyAisFreshness(
  observedAt: string | null | undefined,
  now: number,
): Extract<SeaCurrentPosition['precision'], 'LIVE' | 'RECENT' | 'STALE'> {
  const observedAtMs = validPositionTimestamp(observedAt)
  if (observedAtMs === null || !Number.isFinite(now)) return 'STALE'

  const ageMs = now - observedAtMs
  if (ageMs < 0) return 'STALE'
  if (ageMs <= AIS_LIVE_AGE_MS) return 'LIVE'
  if (ageMs <= AIS_RECENT_AGE_MS) return 'RECENT'
  return 'STALE'
}

export function getRouteStepCoordinates(step: SeaRouteStep) {
  if (typeof step.latitude === 'number' && typeof step.longitude === 'number') {
    return {
      latitude: step.latitude,
      longitude: step.longitude,
    }
  }
  const haystack = `${step.label} ${step.name} ${step.meta ?? ''}`.toLowerCase()
  const known = knownRouteCoordinates.find((entry) =>
    entry.patterns.some((pattern) => haystack.includes(pattern)),
  )
  return known ? { latitude: known.latitude, longitude: known.longitude } : null
}

export function compactRouteLabel(label: string) {
  const normalized = label.toLowerCase()
  if (normalized.includes('receipt')) return 'POR'
  if (normalized === 'pol') return 'POL'
  if (normalized === 'pod') return 'POD'
  if (normalized.includes('delivery')) return 'DEL'
  return label.toUpperCase()
}

export function combineRouteLabels(labels: string[]) {
  if (labels.includes('POR') && labels.includes('POL')) return 'POR/POL'
  if (labels.includes('POD') && labels.includes('DEL')) return 'POD/DEL'
  return labels.join('/')
}

export function buildRoutePathSegments(points: RouteMapPoint[]): RoutePathSegment[] {
  const segments: RoutePathSegment[] = []
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index]!
    const end = points[index + 1]!
    const coordinates = buildShapedRouteSegment(start, end)
    if (coordinates.length < 2) continue
    segments.push({
      fromIndex: index,
      toIndex: index + 1,
      coordinates,
    })
  }
  return segments
}

export function flattenRouteSegments(segments: RoutePathSegment[]) {
  return segments.flatMap((segment, index) =>
    index === 0 ? segment.coordinates : segment.coordinates.slice(1),
  )
}

export function buildShapedRouteSegment(start: RouteMapPoint, end: RouteMapPoint) {
  const corridor = getRouteCorridor(start, end)
  if (corridor) {
    return densifyRouteCoordinates([routeCoordinate(start), ...corridor, routeCoordinate(end)])
  }
  return interpolateGreatCircle(routeCoordinate(start), routeCoordinate(end), 24)
}

export function getRouteCorridor(start: RouteMapPoint, end: RouteMapPoint) {
  for (const corridor of shapedRouteCorridors) {
    const forward = routePointMatches(start, corridor.from) && routePointMatches(end, corridor.to)
    if (forward) return corridor.waypoints
    const reverse = routePointMatches(start, corridor.to) && routePointMatches(end, corridor.from)
    if (reverse) return [...corridor.waypoints].reverse()
  }
  return null
}

export function routePointMatches(point: RouteMapPoint, patterns: string[]) {
  const haystack = `${point.mapLabel} ${point.name} ${point.meta ?? ''}`.toLowerCase()
  return patterns.some((pattern) => haystack.includes(pattern))
}

export function routeCoordinate(point: RouteCoordinate): RouteCoordinate {
  return {
    latitude: point.latitude,
    longitude: point.longitude,
  }
}

export function densifyRouteCoordinates(coordinates: RouteCoordinate[]) {
  return coordinates.flatMap((coordinate, index) => {
    if (index === coordinates.length - 1) return [coordinate]
    const next = coordinates[index + 1]!
    const segment = interpolateGreatCircle(coordinate, next, 8)
    return index === 0 ? segment : segment.slice(1)
  })
}

export function interpolateGreatCircle(
  start: RouteCoordinate,
  end: RouteCoordinate,
  steps: number,
) {
  const lat1 = toRadians(start.latitude)
  const lon1 = toRadians(start.longitude)
  const lat2 = toRadians(end.latitude)
  const lon2 = toRadians(end.longitude)
  const distance =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2,
      ),
    )

  if (!Number.isFinite(distance) || distance < 0.000001) return [start, end]

  const coordinates: RouteCoordinate[] = []
  for (let step = 0; step <= steps; step += 1) {
    const fraction = step / steps
    const a = Math.sin((1 - fraction) * distance) / Math.sin(distance)
    const b = Math.sin(fraction * distance) / Math.sin(distance)
    const x = a * Math.cos(lat1) * Math.cos(lon1) + b * Math.cos(lat2) * Math.cos(lon2)
    const y = a * Math.cos(lat1) * Math.sin(lon1) + b * Math.cos(lat2) * Math.sin(lon2)
    const z = a * Math.sin(lat1) + b * Math.sin(lat2)
    const latitude = toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y)))
    const longitude = normalizeLongitude(toDegrees(Math.atan2(y, x)))
    coordinates.push({ latitude, longitude })
  }
  return coordinates
}

export function toRadians(value: number) {
  return value * (Math.PI / 180)
}

export function toDegrees(value: number) {
  return value * (180 / Math.PI)
}

export function normalizeLongitude(value: number) {
  if (value > 180) return value - 360
  if (value < -180) return value + 360
  return value
}
